import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import { authHeader } from '../../utils/authHeader';
import {
  fetchCompanySubscriptionGA, fetchPlans, fetchFeatures, overrideCompanySubscription,
  grantCompanyPlan, renewCompanyGrant, revokeCompanyGrant,
} from '../../api/planClient';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY_USER_FORM = { username: '', email: '', role: 'user', preferredLanguage: 'en' };

const mapError = (t, err) => {
  if (err.response?.status === 401) return t('ga.error.unauthorized');
  if (err.response?.status === 403) return t('ga.error.forbidden');
  if (err.response?.status === 404) return t('ga.error.actionUnavailable');
  // Validation and state conflicts carry a specific, already-translated
  // message from the server (wrong company name, past date, no grant...).
  if (err.response?.status === 400 || err.response?.status === 409) return err.response?.data?.error || t('ga.error.generic');
  if (err.response?.status >= 500) return err.response?.data?.message || err.response?.data?.error || t('ga.error.server');
  if (!err.response) return t('ga.error.network');
  return t('ga.error.generic');
};

const useGACompanyDetailPage = () => {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const config = useContext(ConfigContext);

  const [status, setStatus] = useState('loading');
  const [company, setCompany] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [users, setUsers] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [changingPlan, setChangingPlan] = useState(false);

  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [userDrawerMode, setUserDrawerMode] = useState('create'); // 'create' | 'edit'
  const [editingUserId, setEditingUserId] = useState(null);
  const [userForm, setUserForm] = useState(EMPTY_USER_FORM);
  const [savingUser, setSavingUser] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    if (!config?.backendUrl) return;
    setStatus('loading');
    try {
      const res = await axios.get(`${config.backendUrl}/ga/companies/${companyId}`, { headers: authHeader() });
      setCompany(res.data.company);
      setUsers(res.data.users);

      const backendMetrics = res.data.metrics || {};
      const [inventoryRes, ordersRes] = await Promise.allSettled([
        axios.get(`${config.backendUrl}/inventory?companyId=${companyId}`, { headers: authHeader() }),
        axios.get(`${config.backendUrl}/orders?companyId=${companyId}`, { headers: authHeader() }),
      ]);

      const inventoryCount = inventoryRes.status === 'fulfilled'
        ? (inventoryRes.value.data?.length ?? backendMetrics.inventoryCount ?? 0)
        : (backendMetrics.inventoryCount ?? 0);

      const orders = ordersRes.status === 'fulfilled' ? (ordersRes.value.data?.orders || []) : null;
      const ordersCount = orders !== null ? orders.length : (backendMetrics.ordersCount ?? 0);

      let lastActivity = backendMetrics.lastActivity;
      if (orders && orders.length > 0) {
        const latest = orders.reduce((a, b) => (new Date(a.createdAt) > new Date(b.createdAt) ? a : b));
        lastActivity = latest.createdAt || null;
      }

      setMetrics({ ...backendMetrics, inventoryCount, ordersCount, lastActivity });

      // Subscription data is additive: a failure here must not break the page.
      const [subRes, plansRes, featuresRes] = await Promise.allSettled([
        fetchCompanySubscriptionGA(config.backendUrl, companyId),
        fetchPlans(config.backendUrl),
        fetchFeatures(config.backendUrl),
      ]);
      setSubscription(subRes.status === 'fulfilled' && subRes.value.data?.current !== undefined ? subRes.value.data : null);
      setPlans(plansRes.status === 'fulfilled' && Array.isArray(plansRes.value.data) ? plansRes.value.data : []);
      setFeatures(featuresRes.status === 'fulfilled' && Array.isArray(featuresRes.value.data) ? featuresRes.value.data : []);
      setStatus('ready');
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setStatus('permission');
      } else {
        setStatus('error');
        notify(mapError(t, error), 'error');
      }
    }
  }, [config, companyId, t]);

  useEffect(() => { load(); }, [load]);

  const userErrors = useMemo(() => {
    const errs = {};
    if (!userForm.username.trim()) errs.username = t('ga.validation.required');
    if (!EMAIL_RE.test(userForm.email)) errs.email = t('ga.validation.email');
    return errs;
  }, [userForm.username, userForm.email, t]);
  const isUserValid = Object.keys(userErrors).length === 0;

  const openAddUserDrawer = () => {
    setUserDrawerMode('create');
    setEditingUserId(null);
    setUserForm(EMPTY_USER_FORM);
    setUserDrawerOpen(true);
  };
  const openEditUserDrawer = (user) => {
    setUserDrawerMode('edit');
    setEditingUserId(user._id);
    setUserForm({
      username: user.username || '',
      email: user.email || '',
      role: user.role || 'user',
      preferredLanguage: user.preferredLanguage || 'en',
    });
    setUserDrawerOpen(true);
  };
  const closeUserDrawer = () => {
    if (savingUser) return;
    setUserDrawerOpen(false);
  };

  // One drawer, two verbs: POST creates a user in this company, PUT edits the
  // one being viewed (the server also moves a role change onto the membership).
  const saveUser = async () => {
    if (!isUserValid || savingUser) return;
    setSavingUser(true);
    try {
      if (userDrawerMode === 'edit') {
        await axios.put(`${config.backendUrl}/ga/users/${editingUserId}`, userForm, { headers: authHeader() });
        notify(t('ga.company.userUpdated'));
      } else {
        await axios.post(`${config.backendUrl}/ga/companies/${companyId}/users`, userForm, { headers: authHeader() });
        notify(t('ga.company.addUser'));
      }
      setUserDrawerOpen(false);
      load();
    } catch (error) {
      notify(mapError(t, error), 'error');
    } finally {
      setSavingUser(false);
    }
  };

  const openDeleteUser = (user) => setDeleteTarget(user);
  const closeDeleteUser = () => {
    if (deletingUser) return;
    setDeleteTarget(null);
  };
  const confirmDeleteUser = async () => {
    if (!deleteTarget || deletingUser) return;
    setDeletingUser(true);
    try {
      await axios.delete(`${config.backendUrl}/ga/users/${deleteTarget._id}`, { headers: authHeader() });
      notify(t('ga.company.userDeleted'));
      setDeleteTarget(null);
      load();
    } catch (error) {
      notify(mapError(t, error), 'error');
    } finally {
      setDeletingUser(false);
    }
  };

  // Every subscription action resolves to true on success so the card can
  // close its drawer, and false on failure so the form (and the user's
  // typing) stays put behind the error snackbar.
  const runSubscriptionAction = async (fn, successMessage) => {
    setChangingPlan(true);
    try {
      await fn();
      notify(successMessage);
      load();
      return true;
    } catch (error) {
      notify(mapError(t, error), 'error');
      return false;
    } finally {
      setChangingPlan(false);
    }
  };

  const changePlan = (planId) => runSubscriptionAction(
    () => overrideCompanySubscription(config.backendUrl, companyId, planId),
    t('Plan updated', { defaultValue: 'Plan updated' }),
  );
  const grantPlan = (body) => runSubscriptionAction(
    () => grantCompanyPlan(config.backendUrl, companyId, body),
    t('Partner plan granted', { defaultValue: 'Partner plan granted' }),
  );
  const renewGrant = (body) => runSubscriptionAction(
    () => renewCompanyGrant(config.backendUrl, companyId, body),
    t('Grant renewed', { defaultValue: 'Grant renewed' }),
  );
  const revokeGrant = (reason) => runSubscriptionAction(
    () => revokeCompanyGrant(config.backendUrl, companyId, reason),
    t('Grant revoked — company is back on the default plan', { defaultValue: 'Grant revoked — company is back on the default plan' }),
  );

  const patchStatus = async (newStatus) => {
    try {
      await axios.patch(`${config.backendUrl}/ga/companies/${companyId}`, { status: newStatus }, { headers: authHeader() });
      load();
    } catch (error) {
      notify(mapError(t, error), 'error');
    }
  };

  return {
    t, companyId, status, company, metrics, users, load,
    userDrawerOpen, userForm, setUserForm, userErrors, isUserValid, savingUser,
    openAddUserDrawer, openEditUserDrawer, closeUserDrawer, saveUser, patchStatus,
    userDrawerMode, deleteTarget, deletingUser, openDeleteUser, closeDeleteUser, confirmDeleteUser,
    subscription, plans, features, changingPlan, changePlan, grantPlan, renewGrant, revokeGrant,
    snackbar, setSnackbar,
  };
};

export default useGACompanyDetailPage;
